<script setup lang="ts">
import { computed } from 'vue';
import { customTabsCollections } from '~/utils/customTabsConfigs';
import top10newgames from '~/assets/json/top10newgames.json';
import roletasMaisJogadasData from '~/assets/json/roletas-mais-jogadas.json';

interface Props {
  items: HomeSectionItem[];
  loading: boolean;
  aboveFoldCount?: number; // Quantos items são above-the-fold (default: 3)
}

const { items, loading, aboveFoldCount = 3 } = defineProps<Props>();
const { isDesktop, isMobile } = useLayout();

// ============================================================================
// LAZY LOADING STRATEGY FOR MOBILE
// ============================================================================

// Slugs das seções que devem carregar imediatamente no mobile
const PRIORITY_SECTIONS_MOBILE = [
  'queridinhos',
  'banners',
  'information-banners',
  'informacoes',
  'provedoras-de-jogos',
  'provedoras',
];

// Verifica se uma seção é prioritária no mobile
function isPrioritySectionMobile(item: HomeSectionItem): boolean {
  if (!isMobile.value) return true; // Desktop carrega tudo normalmente

  const slug = normalizeSlug(item.data.slug);

  return PRIORITY_SECTIONS_MOBILE.includes(slug);
}

// ============================================================================
// NORMALIZAÇÃO DE IMAGENS
// ============================================================================

// Remove /public, /mobile ou /ipad do final da URL se existir
// (pois o componente adiciona esses sufixos dinamicamente)
function normalizeImageUrl(url: string): string {
  if (!url) return url;

  return url.replace(/\/(public|mobile|ipad)$/, '');
}

// ============================================================================
// MAPEAMENTO DE SLUGS PARA COMPONENTES ESPECIAIS (PLACEHOLDER)
// ============================================================================

type PlaceholderType =
  | 'perfil-oficial'
  | 'tournaments'
  | 'banners'
  | 'novidades'
  | 'roletas'
  | 'crash'
  | 'vip'
  | 'faq'
  | 'favoritos'
  | 'recentemente-jogados'
  | 'provedoras'
  | 'jogos-em-destaque';

const PLACEHOLDER_KEYWORD_RULES: {
  type: PlaceholderType;
  keywords: string[];
}[] = [
  {
    type: 'recentemente-jogados',
    keywords: [
      'recente',
      'recently',
      'jogados',
      'vistos',
      'last-game',
      'last-played',
      'recent',
    ],
  },
  {
    type: 'favoritos',
    keywords: ['favorit', 'favorite', 'favourit'],
  },
  {
    type: 'perfil-oficial',
    keywords: ['perfil-oficial', 'perfis-oficiais', 'perfil-oficia'],
  },
  {
    type: 'tournaments',
    keywords: ['torneio', 'tournament'],
  },
  {
    type: 'banners',
    keywords: ['banner', 'information-banner', 'informacoe'],
  },
  {
    type: 'novidades',
    keywords: ['novidade', 'new-game'],
  },
  {
    type: 'roletas',
    keywords: ['roleta', 'roulette'],
  },
  {
    type: 'crash',
    keywords: ['crash'],
  },
  {
    type: 'vip',
    keywords: ['vip'],
  },
  {
    type: 'faq',
    keywords: ['faq', 'duvida', 'duvidas-frequentes'],
  },
  {
    type: 'provedoras',
    keywords: ['provedora', 'provider'],
  },
  {
    type: 'jogos-em-destaque',
    keywords: ['jogos-em-destaque', 'jogos-destaque', 'destaque-esport'],
  },
];

function getPlaceholderType(slug: string): PlaceholderType | null {
  const normalized = slug.toLowerCase();

  for (const rule of PLACEHOLDER_KEYWORD_RULES) {
    if (rule.keywords.some((kw) => normalized.includes(kw))) {
      return rule.type;
    }
  }

  return null;
}

// ============================================================================
// DADOS COMPUTADOS
// ============================================================================

// Ordena items por priority
const sortedItems = computed(() => {
  return [...items].sort((a, b) => a.priority - b.priority);
});

// Dados das roletas processados (imagens locais no mobile)
const roletasMaisJogadas = computed(() => {
  if (!isDesktop.value) {
    return {
      ...roletasMaisJogadasData,
      games: roletasMaisJogadasData.games.map((game, index) => ({
        ...game,
        image: `/assets/images/top-10-roletas/${index + 1}.avif`,
        useCdnOnMobile: false,
      })),
    };
  }

  // Para desktop, adicionar imagem vazia ou placeholder (será buscada via CDN)
  return {
    ...roletasMaisJogadasData,
    games: roletasMaisJogadasData.games.map((game, index) => ({
      ...game,
      image: `/assets/images/top-10-roletas/${index + 1}.avif`,
    })),
  };
});

// ============================================================================
// MAPPERS - Transformam dados da API para formato dos componentes
// ============================================================================

/**
 * Normaliza slug para lowercase
 */
function normalizeSlug(slug: string | null | undefined): string {
  return slug?.toLowerCase() || '';
}

function mapGamesToPerfilOficialFormat(games: HomeGame[]): GameItem[] {
  return games
    .sort((a, b) => a.pivot.priority - b.pivot.priority)
    .map((game) => ({
      id: String(game.id),
      name: game.name,
      slug: normalizeSlug(game.slug),
      image: normalizeImageUrl(game.image),
      ribbon: game.ribbon ?? null,
      tags: game.tags ?? null,
      provider: game.provider
        ? { id: game.provider.id, name: game.provider.name }
        : null,
    }));
}

function formatTitleClass(title: string): string {
  return (
    'title-' +
    title
      .toLowerCase()
      .replaceAll(/\s+/g, '-')
      .replaceAll(/[^a-z0-9-]/g, '')
  );
}

// Aplica limit se definido no item
function applyLimit<T>(arr: T[], limit: number | null): T[] {
  if (limit === null || limit <= 0) return arr;

  return arr.slice(0, limit);
}

// Quando a API não retorna see_more_url para uma tab e todos os jogos dela
// pertencem ao mesmo provider, deriva a URL no padrão das outras tabs:
// /games/category/all?initialCategory=<parent>&initialProvider=<provider>
function deriveTabSeeMoreUrl(
  category: HomeTabCategory,
  parentSlug: string,
): string | null {
  if (category.item_config?.see_more_url) {
    return category.item_config.see_more_url;
  }

  const games = category.games || [];

  if (games.length === 0) return null;

  const firstProviderSlug = games[0]?.provider?.slug;

  if (!firstProviderSlug) return null;

  const allSameProvider = games.every(
    (g) => g.provider?.slug === firstProviderSlug,
  );

  if (!allSameProvider) return null;

  const parent = encodeURIComponent(parentSlug.toLowerCase());
  const provider = encodeURIComponent(firstProviderSlug);

  return `/games/category/all?initialCategory=${parent}&initialProvider=${provider}`;
}

// Mapeia categories para o formato de tabs esperado pelo TabsApostatudo
function mapCategoriesToTabs(
  categories: HomeTabCategory[],
  parentSlug = '',
): Array<{
  title: string;
  child: GameItem[];
  icon?: string;
  slug?: string;
  see_more_url?: string | null;
}> {
  return categories.map((category) => ({
    title: category.name,
    slug: category.slug?.toLowerCase() || undefined, // Normaliza slug para lowercase
    icon: category.item_config?.icon || undefined,
    see_more_url: deriveTabSeeMoreUrl(category, parentSlug), // URL customizada do "Ver Mais"
    child: category.games
      .sort((a, b) => a.pivot.priority - b.pivot.priority)
      .map((game) => ({
        id: String(game.id),
        name: game.name,
        slug: game.slug.toLowerCase(),
        image: normalizeImageUrl(game.image),
        ribbon: game.ribbon ?? null,
        tags: game.tags ?? null,
        provider: game.provider
          ? { id: game.provider.id, name: game.provider.name }
          : null,
      })),
  }));
}

// Verifica se uma categoria é do tipo PLACEHOLDER
function isCategoryPlaceholder(category: HomeTabCategory): boolean {
  return category.item_config?.content_type === 'PLACEHOLDER';
}

// Verifica se todas as categorias são placeholders
function areAllCategoriesPlaceholders(categories: HomeTabCategory[]): boolean {
  return categories.every((cat) => isCategoryPlaceholder(cat));
}

// Mapeia categories placeholders para o formato de TabsPlaceholder
function mapCategoriesToPlaceholderTabs(categories: HomeTabCategory[]): Array<{
  id: number;
  name: string;
  slug: string;
  placeholderType: 'favoritos' | 'recentemente-jogados' | null;
}> {
  return categories.map((category) => {
    const placeholderType = getPlaceholderType(category.slug) as
      | 'favoritos'
      | 'recentemente-jogados'
      | null;

    // Usa nomes curtos para as tabs
    let shortName = category.name;

    if (placeholderType === 'favoritos') {
      shortName = 'Favoritos';
    } else if (placeholderType === 'recentemente-jogados') {
      shortName = 'Recentes';
    }

    return {
      id: category.id,
      name: shortName,
      slug: category.slug,
      placeholderType,
    };
  });
}

// Type guard para verificar se data é HomeTabsData
function isHomeTabsData(
  data: HomeCategoryData | HomeTabsData,
): data is HomeTabsData {
  return 'categories' in data;
}

// Type guard para verificar se data é HomeCategoryData
function isHomeCategoryData(
  data: HomeCategoryData | HomeTabsData,
): data is HomeCategoryData {
  return 'games' in data;
}

// ============================================================================
// ABOVE-THE-FOLD DETECTION
// ============================================================================

// Helper para verificar se um item está above-the-fold
// No desktop: usa o índice (primeiros N itens)
// No mobile: verifica se é uma seção prioritária (Queridinhos, Banners, Provedoras)
function isAboveFold(index: number, item?: HomeSectionItem): boolean {
  if (isDesktop.value) {
    return index < aboveFoldCount;
  }

  // Mobile: considera above-the-fold apenas seções prioritárias
  if (item && isPrioritySectionMobile(item)) {
    return true;
  }

  return false;
}

// ============================================================================
// CORREÇÃO DE ÍCONES BASEADO EM SLUG/NOME
// ============================================================================
// Retorna o ícone correto baseado no slug ou nome da categoria
// Usado como fallback quando a API não envia um ícone (icon === null)
function getIconForCategory(slug: string, name: string): string {
  const slugLower = slug.toLowerCase();
  const nameLower = name.toLowerCase();

  // Queridinhos
  if (slugLower.includes('queridinhos') || nameLower.includes('queridinhos')) {
    return 'home/queridinhos';
  }

  // Novidades
  if (
    slugLower.includes('novidades') ||
    nameLower.includes('novidades') ||
    slugLower.includes('new-games') ||
    slugLower.includes('jogos-novos')
  ) {
    return 'home/mais-jogados';
  }

  // Slots
  if (slugLower.includes('slots') || nameLower.includes('slots')) {
    return 'home/slots';
  }

  // Default
  return 'home/slots';
}
</script>

<template>
  <!-- Loading skeletons -->
  <template v-if="loading">
    <template
      v-for="i in 3"
      :key="i"
    >
      <CasinoMediaList
        class="mb-4"
        display-mode="slide"
        :is-loading="true"
        :per-row="{ 768: 3, 1100: 4, 1200: 5, desktop: 6 }"
        :space-between="[22, 16, 10]"
      />
    </template>
  </template>

  <!-- Renderização dinâmica por content_type -->
  <template v-else>
    <template
      v-for="(item, index) in sortedItems"
      :key="`${item.type}-${item.data.id}-${item.priority}`"
    >
      <!-- ================================================================ -->
      <!-- NUMBER: Swiper com números (Top 10, Mais Jogados, etc)           -->
      <!-- ================================================================ -->

      <!-- NUMBER - Above-the-fold (3 primeiros) -->
      <PerfilOficialMediaList
        v-if="
          isAboveFold(index, item) &&
          item.content_type === 'NUMBER' &&
          isHomeCategoryData(item.data) &&
          item.data.games.length > 0
        "
        :button="{
          url:
            item.see_more_url ||
            `/games/category/${normalizeSlug(item.data.slug)}`,
          text: 'VER MAIS',
        }"
        :class="['mb-4', formatTitleClass(item.data.name)]"
        disable-lazy-loading
        display-mode="slide"
        :icon-path="
          item.icon || getIconForCategory(item.data.slug, item.data.name)
        "
        :items="
          mapGamesToPerfilOficialFormat(applyLimit(item.data.games, item.limit))
        "
        loading-numbers="eager"
        :mobile-eager-count="3"
        :per-row="{ 500: 3.3, 990: 5.1, desktop: 6 }"
        :see-more-url="
          item.see_more_url ||
          `/games/category/${normalizeSlug(item.data.slug)}`
        "
        :show-numbers="true"
        :space-between="[22, 16, 10]"
        :title="item.data.name"
      />

      <!-- NUMBER - Below-the-fold (demais) -->
      <UtilsLazyRender
        v-else-if="
          !isAboveFold(index, item) &&
          item.content_type === 'NUMBER' &&
          isHomeCategoryData(item.data) &&
          item.data.games.length > 0
        "
        min-height="300px"
      >
        <LazyPerfilOficialMediaList
          :button="{
            url:
              item.see_more_url ||
              `/games/category/${normalizeSlug(item.data.slug)}`,
            text: 'VER MAIS',
          }"
          :class="['mb-4', formatTitleClass(item.data.name)]"
          display-mode="slide"
          :icon-path="
            item.icon || getIconForCategory(item.data.slug, item.data.name)
          "
          :items="
            mapGamesToPerfilOficialFormat(
              applyLimit(item.data.games, item.limit),
            )
          "
          :per-row="{ 500: 3.3, 990: 5.1, desktop: 6 }"
          :see-more-url="
            item.see_more_url ||
            `/games/category/${normalizeSlug(item.data.slug)}`
          "
          :show-numbers="true"
          :space-between="[22, 16, 10]"
          :title="item.data.name"
        />
      </UtilsLazyRender>

      <!-- ================================================================ -->
      <!-- BASIC: Swiper padrão de jogos                                    -->
      <!-- ================================================================ -->

      <!-- BASIC renderiza como TabsApostatudo com aba única oculta, igual ao
           TABS type="category" — herda o mesmo tamanho de card dos demais
           swipers da home. mobile-rows=1 mantém linha única no mobile (sem isso
           o TabsApostatudo quebra em 2 linhas). -->
      <SectionsTabsApostatudo
        v-if="
          item.content_type === 'BASIC' &&
          isHomeCategoryData(item.data) &&
          item.data.games.length > 0
        "
        :header-icon-path="
          item.icon || getIconForCategory(item.data.slug, item.data.name)
        "
        :header-see-more-url="
          item.see_more_url ||
          `/games/category/${normalizeSlug(item.data.slug)}`
        "
        :header-title="item.data.name"
        :is-tab-visible="false"
        :loading-strategy="isAboveFold(index, item) ? 'eager' : 'lazy'"
        :mobile-rows="1"
        :tabs="[
          {
            title: item.data.name,
            slug: item.data.slug,
            icon: item.icon || undefined,
            see_more_url: item.see_more_url || null,
            child: mapGamesToPerfilOficialFormat(
              applyLimit(item.data.games, item.limit),
            ),
          },
        ]"
      />

      <!-- ================================================================ -->
      <!-- TABS: Swiper com abas                                            -->
      <!-- ================================================================ -->

      <!-- TABS type="category": jogos em data.games (renderiza como TabsApostatudo sem tabs visíveis) -->
      <SectionsTabsApostatudo
        v-if="
          item.content_type === 'TABS' &&
          item.type === 'category' &&
          isHomeCategoryData(item.data) &&
          item.data.games.length > 0
        "
        :header-icon-path="
          item.icon || getIconForCategory(item.data.slug, item.data.name)
        "
        :header-see-more-url="
          item.see_more_url ||
          `/games/category/${normalizeSlug(item.data.slug)}`
        "
        :header-title="item.data.name"
        :is-tab-visible="false"
        :loading-strategy="isAboveFold(index, item) ? 'eager' : 'lazy'"
        :tabs="[
          {
            title: item.data.name,
            slug: item.data.slug,
            icon: item.icon || undefined,
            see_more_url: item.see_more_url || null,
            child: mapGamesToPerfilOficialFormat(
              applyLimit(item.data.games, item.limit),
            ),
          },
        ]"
      />

      <!-- TABS type="section" com categorias PLACEHOLDER: renderiza com TabsPlaceholder -->
      <SectionsTabsPlaceholder
        v-else-if="
          item.content_type === 'TABS' &&
          item.type === 'section' &&
          isHomeTabsData(item.data) &&
          areAllCategoriesPlaceholders(item.data.categories)
        "
        :header-title="item.data.name"
        :tabs="mapCategoriesToPlaceholderTabs(item.data.categories)"
      />

      <!-- TABS type="section" com apenas 1 categoria: renderiza como TabsApostatudo sem tabs visíveis -->
      <SectionsTabsApostatudo
        v-else-if="
          item.content_type === 'TABS' &&
          item.type === 'section' &&
          isHomeTabsData(item.data) &&
          item.data.categories.length === 1 &&
          item.data.categories[0] &&
          item.data.categories[0].games &&
          item.data.categories[0].games.length > 0
        "
        :header-icon-path="
          item.data.categories[0]?.item_config?.icon ||
          item.icon ||
          getIconForCategory(
            item.data.categories[0]?.slug || item.data.slug,
            item.data.categories[0]?.name || item.data.name,
          )
        "
        :header-see-more-url="
          item.data.categories[0]?.item_config?.see_more_url ||
          item.see_more_url ||
          `/games/category/${normalizeSlug(item.data.categories[0]?.slug) || 'all'}`
        "
        :header-title="item.data.name"
        :is-tab-visible="false"
        :loading-strategy="isAboveFold(index, item) ? 'eager' : 'lazy'"
        :tabs="mapCategoriesToTabs(item.data.categories, item.data.slug)"
      />

      <!-- TABS type="section" com múltiplas categorias: renderiza com tabs visíveis -->
      <SectionsTabsApostatudo
        v-else-if="
          item.content_type === 'TABS' &&
          item.type === 'section' &&
          isHomeTabsData(item.data) &&
          item.data.categories.length > 1
        "
        :header-icon-path="
          item.icon || getIconForCategory(item.data.slug, item.data.name)
        "
        :header-see-more-url="item.see_more_url || undefined"
        :header-title="item.data.name"
        :is-tab-visible="true"
        :loading-strategy="isAboveFold(index, item) ? 'eager' : 'lazy'"
        :tabs="mapCategoriesToTabs(item.data.categories, item.data.slug)"
      />

      <!-- ================================================================ -->
      <!-- PLACEHOLDER: Componentes especiais baseados no slug              -->
      <!-- ================================================================ -->
      <template v-else-if="item.content_type === 'PLACEHOLDER'">
        <!-- ==================== JOGOS FAVORITOS ==================== -->
        <CasinoFavoriteGamesList
          v-if="getPlaceholderType(item.data.slug) === 'favoritos'"
          :always-show="true"
          :scale-factor="isDesktop ? 1 : 0.573"
          :title="item.data.name"
        />

        <!-- ==================== JOGADOS RECENTEMENTE ==================== -->
        <CasinoLastGamesPlayed
          v-else-if="
            getPlaceholderType(item.data.slug) === 'recentemente-jogados'
          "
          :always-show="true"
        />

        <!-- ==================== PERFIL OFICIAL ==================== -->
        <!-- Above-the-fold -->
        <SectionsPerfilOficialList
          v-if="
            isAboveFold(index, item) &&
            getPlaceholderType(item.data.slug) === 'perfil-oficial'
          "
          :button="{ to: '/perfil-oficial', text: 'VER MAIS' }"
          class="mb-4"
          display-mode="slide"
          icon="home/mais-jogados"
          load-from-config
          :per-row="[6, 5, 3]"
          :space-between="[8, 8, 8]"
          :title="item.data.name || 'Perfis Oficiais'"
        />
        <!-- Below-the-fold -->
        <UtilsLazyRender
          v-else-if="
            !isAboveFold(index, item) &&
            getPlaceholderType(item.data.slug) === 'perfil-oficial'
          "
          min-height="300px"
        >
          <LazySectionsPerfilOficialList
            :button="{ to: '/perfil-oficial', text: 'VER MAIS' }"
            class="mb-4"
            display-mode="slide"
            icon="home/mais-jogados"
            load-from-config
            :per-row="[6, 5, 3]"
            :space-between="[8, 8, 8]"
            :title="item.data.name || 'Perfis Oficiais'"
          />
        </UtilsLazyRender>

        <!-- ==================== TORNEIOS ==================== -->
        <!-- Above-the-fold -->
        <HomeTournaments
          v-if="
            isAboveFold(index, item) &&
            getPlaceholderType(item.data.slug) === 'tournaments'
          "
        />
        <!-- Below-the-fold -->
        <UtilsLazyRender
          v-else-if="
            !isAboveFold(index, item) &&
            getPlaceholderType(item.data.slug) === 'tournaments'
          "
          min-height="350px"
        >
          <LazyHomeTournaments />
        </UtilsLazyRender>

        <!-- ==================== BANNERS INFORMATIVOS ==================== -->
        <!-- Mobile only: Desktop usa SectionsSliderApostatudo fixo no topo -->
        <SectionsInformationBanners
          v-if="!isDesktop && getPlaceholderType(item.data.slug) === 'banners'"
          :is-above-fold="isAboveFold(index, item)"
        />

        <!-- ==================== NOVIDADES ==================== -->
        <!-- Above-the-fold -->
        <PerfilOficialMediaList
          v-if="
            isAboveFold(index, item) &&
            getPlaceholderType(item.data.slug) === 'novidades'
          "
          :button="{ url: '/games/category/jogos-novos', text: 'VER MAIS' }"
          class="mb-4"
          disable-lazy-loading
          display-mode="slide"
          :icon-path="item.icon || 'home/mais-jogados'"
          :items="top10newgames"
          loading-numbers="eager"
          :mobile-eager-count="3"
          :per-row="{ 500: 3.3, 990: 5.1, desktop: 6 }"
          see-more-url="/games/category/all?initialCategory=jogos-novos"
          :show-numbers="true"
          :space-between="[22, 16, 10]"
          :title="item.data.name || 'Novidades'"
        />
        <!-- Below-the-fold -->
        <UtilsLazyRender
          v-else-if="
            !isAboveFold(index, item) &&
            getPlaceholderType(item.data.slug) === 'novidades'
          "
          min-height="300px"
        >
          <LazyPerfilOficialMediaList
            :button="{ url: '/games/category/jogos-novos', text: 'VER MAIS' }"
            class="mb-4"
            display-mode="slide"
            :icon-path="item.icon || 'home/mais-jogados'"
            :items="top10newgames"
            :per-row="{ 500: 3.3, 990: 5.1, desktop: 6 }"
            see-more-url="/games/category/all?initialCategory=jogos-novos"
            :show-numbers="true"
            :space-between="[22, 16, 10]"
            :title="item.data.name || 'Novidades'"
          />
        </UtilsLazyRender>

        <!-- ==================== ROLETAS MAIS JOGADAS ==================== -->
        <!-- SEMPRE Above-the-fold (seção premium com imagens otimizadas) -->
        <PerfilOficialMediaList
          v-if="getPlaceholderType(item.data.slug) === 'roletas'"
          :button="{ url: '/perfil-oficial/roletas', text: 'VER MAIS' }"
          :class="[
            'mb-4',
            formatTitleClass(item.data.name || roletasMaisJogadas.title),
          ]"
          disable-lazy-loading
          display-mode="slide"
          :icon-path="roletasMaisJogadas.iconPath"
          :items="roletasMaisJogadas.games"
          loading-numbers="eager"
          :mobile-eager-count="3"
          :per-row="{ 500: 3.3, 990: 5.1, desktop: 6 }"
          see-more-url="/perfil-oficial/roletas"
          :show-numbers="true"
          :space-between="[22, 16, 10]"
          :title="item.data.name || roletasMaisJogadas.title"
        />

        <!-- ==================== CRASH ==================== -->
        <!-- SEMPRE Below-the-fold (componente complexo) -->
        <UtilsLazyRender
          v-if="getPlaceholderType(item.data.slug) === 'crash'"
          min-height="350px"
        >
          <LazySectionsTabsApostatudo
            header-icon-path="home/ganhos-nas-alturas"
            header-see-more-url="/games/category/jogos-crash"
            :header-title="item.data.name || 'Crash'"
            :is-tab-visible="false"
            :tabs="customTabsCollections.colecao4"
          />
        </UtilsLazyRender>

        <!-- ==================== ÁREA VIP ==================== -->
        <!-- SEMPRE Below-the-fold (componente complexo com dados externos) -->
        <UtilsLazyRender
          v-if="getPlaceholderType(item.data.slug) === 'vip'"
          min-height="350px"
        >
          <LazySectionsTabsVIP
            :header-icon-path="item.icon ?? 'home/tournament'"
            header-see-more-url="/vip"
            :header-title="item.data.name || 'Área VIP'"
          />
        </UtilsLazyRender>

        <!-- ==================== DÚVIDAS FREQUENTES (FAQ) ==================== -->
        <!-- SEMPRE Below-the-fold (dados externos via /api/v1/faq) -->
        <UtilsLazyRender
          v-else-if="getPlaceholderType(item.data.slug) === 'faq'"
          min-height="350px"
        >
          <LazySectionsTabsFAQ
            :header-icon-path="item.icon ?? 'home/duvidas'"
            :header-title="item.data.name || 'Dúvidas Frequentes'"
          />
        </UtilsLazyRender>

        <!-- ==================== PROVEDORAS DE JOGOS ==================== -->
        <!-- Visível apenas no mobile/tablet (oculto via CSS no desktop) -->
        <SectionsProvidersSection
          v-else-if="getPlaceholderType(item.data.slug) === 'provedoras'"
        />

        <!-- ==================== JOGOS EM DESTAQUE (curated events) ==================== -->
        <SportsCuratedEventsList
          v-if="getPlaceholderType(item.data.slug) === 'jogos-em-destaque'"
        />

        <!-- Slug não reconhecido: não renderiza (ou debug em dev) -->
        <!-- <div v-else class="debug">Placeholder: {{ item.data.slug }}</div> -->
      </template>
    </template>
  </template>
</template>
